import React from 'react';
import type { ColumnDef, StockFeatures } from '@tanstack/react-table';
import { DataTable, Text, TooltipTrigger, Tooltip } from '@chatsdet/ui';
import { useRowLinkBuilder } from '../../../lib/useRowLink';
import type {
  ExternalTestCatalogRecentRun,
  ExternalTestCatalogRow,
  ExternalTestCatalogSortBy,
} from '../../../features/external-tests/useExternalTestCatalog';
import { formatRelativeTime } from '../FlowDesigner/runs/run-formatters';
import { formatUpdatedAt } from '../FlowDesigner/utils';
import { ExternalTestCatalogRecentRunsCell } from './ExternalTestCatalogRecentRunsCell';

function suitesLabel(row: ExternalTestCatalogRow): string {
  const names = row.suites.map((suite) => suite.name);
  if (names.length === 0) return '—';
  const extra = row.suiteCount - names.length;
  return `${names.join(', ')}${extra > 0 ? ` +${extra}` : ''}`;
}

export function ExternalTestsCatalogTable({
  rows,
  recentRunsByTest,
  isRecentRunsLoading,
  isRecentRunsError,
  pagination,
  isLoading,
  sortBy,
  sortOrder,
  onPageChange,
  onPageSizeChange,
  onSortChange,
  containerClassName,
}: {
  rows: ExternalTestCatalogRow[];
  recentRunsByTest: Record<string, ExternalTestCatalogRecentRun[]>;
  isRecentRunsLoading: boolean;
  isRecentRunsError: boolean;
  pagination?: { page: number; pageSize: number; totalItems: number; totalPages: number };
  isLoading: boolean;
  sortBy?: ExternalTestCatalogSortBy;
  sortOrder?: 'asc' | 'desc';
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onSortChange: (sortBy: string | undefined, sortOrder: 'asc' | 'desc' | undefined) => void;
  containerClassName?: string;
}) {
  const buildHref = useRowLinkBuilder();
  const columns = React.useMemo<ColumnDef<StockFeatures, ExternalTestCatalogRow>[]>(
    () => [
      {
        accessorKey: 'displayName',
        header: 'Test',
        enableSorting: true,
        cell: ({ row }) => (
          <TooltipTrigger delay={300}>
            <span className="block min-w-0 truncate text-sm font-medium">
              {row.original.displayName}
            </span>
            <Tooltip variant="dark">
              <div>{row.original.displayName}</div>
              <div className="text-muted-foreground">{row.original.testKey}</div>
            </Tooltip>
          </TooltipTrigger>
        ),
        meta: { flex: 3 },
      },
      {
        id: 'suite',
        header: 'Suite',
        enableSorting: false,
        cell: ({ row }) => {
          const label = suitesLabel(row.original);
          return (
            <Text size="sm" className="truncate" title={label}>
              {label}
            </Text>
          );
        },
        meta: { flex: 1, maxWidth: 180 },
      },
      {
        // accessorKey matches the server sort key `reliability` so header
        // clicks and the incoming sortBy stay in sync with DataTable's
        // internal sorting state (react-table needs a real accessor for
        // `getCanSort()` to report true, even under manual/server sorting).
        accessorKey: 'reliability',
        header: 'Recent runs',
        enableSorting: true,
        // minWidth fits the 10-slot strip plus the health icon and pass rate;
        // without it the trailing content overflows under the neighboring cell.
        meta: { flex: 1.2, minWidth: 192, maxWidth: 208 },
        cell: ({ row }) => (
          <ExternalTestCatalogRecentRunsCell
            runs={recentRunsByTest[row.original.id]}
            health={row.original.health}
            isLoading={isRecentRunsLoading}
            isError={isRecentRunsError}
          />
        ),
      },
      {
        accessorKey: 'lastSeenAt',
        header: 'Last run',
        enableSorting: true,
        cell: ({ row }) => (
          <TooltipTrigger delay={200}>
            <span className="text-muted-foreground block text-right text-sm tabular-nums cofactor:font-mono cofactor:text-[11px] cursor-default">
              {formatRelativeTime(row.original.lastSeenAt)}
            </span>
            <Tooltip variant="dark">{formatUpdatedAt(row.original.lastSeenAt)}</Tooltip>
          </TooltipTrigger>
        ),
        meta: { flex: 0.6, maxWidth: 110 },
      },
    ],
    [recentRunsByTest, isRecentRunsLoading, isRecentRunsError]
  );

  return (
    <DataTable
      columns={columns}
      data={rows}
      aria-label="External tests catalog"
      containerClassName={containerClassName}
      getRowHref={(row) =>
        row.latestRunId && row.latestItemId
          ? buildHref({
              to: '/runs/external/$runId/item/$itemId',
              params: { runId: row.latestRunId, itemId: row.latestItemId },
            })
          : row.latestRunId
            ? buildHref({ to: '/runs/external/$runId', params: { runId: row.latestRunId } })
            : undefined
      }
      serverPagination={
        pagination
          ? {
              ...pagination,
              pageCount: pagination.totalPages,
              onPageChange,
              onPageSizeChange,
              pageSizeOptions: [25, 50, 100],
              isLoading,
            }
          : undefined
      }
      serverSorting={{
        sortBy: sortBy ?? 'lastSeenAt',
        sortOrder: sortOrder ?? 'desc',
        onSortChange,
      }}
    />
  );
}
